// Generated by TaroBase SDK Generator: 2026-07-18T20:30:00.103Z
// Collection: payments/$paymentId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Onchain USDC payment record for a completed plastic-recovery listing. paymentId MUST equal the listingId (1:1). The accepting recycling company creates this to move USDC to the household. isPassthrough: the transfer executes atomically onchain but no permanent onchain account is stored (queryable offchain). The listings collection gates its completion transition on the existence of this record, so a listing can never be marked 'completed' without a successful payment.
 */

export interface PaymentsRequest {
  payer: AddressType;
  recipient: AddressType;
  amt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface PaymentsResponse {
  payer: string;
  recipient: string;
  amt: number;
  id: string;
  tarobase_created_at: number;
  tarobase_transaction_hash?: string | undefined; // Represents the hash of the transaction that created this item. Can be used to create Solscan links (combine with the current network in the Tarobase app)
}

/**
 * Build a Payments operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildPayments(paymentId: string, data?: PaymentsRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `payments/${paymentId}`, document: doc };
}

/**
 * Authenticated recycling company only. Caller (@user.address) must equal @newData.payer. Onchain hook transfers @newData.amt of USDC (base units, 6 decimals) from payer to @newData.recipient (the household). paymentId must equal the listingId being completed. amt must equal the listing's estimatedValue (cents) * 10000 to convert cents to USDC micro-units. recipient must differ from payer and amt must be > 0. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setPayments(paymentId: string, data?: PaymentsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`payments/${paymentId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Payments: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read payment records (public, links a listing payment to its onchain transfer).
   (Get Single Item)
 */
export async function getPayments(paymentId: string): Promise<PaymentsResponse | null> {
  try {
    const result = await get(`payments/${paymentId}`);
    return result as PaymentsResponse | null;
  } catch (error) {
    console.error(`Error getting Payments: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Payments document. (
  Read Operation Details: Anyone can read payment records (public, links a listing payment to its onchain transfer).
  )
 */
export function subscribePayments(callback: (data: PaymentsResponse | null) => void, paymentId: string): Promise<() => Promise<void>> {
  return subscribe(`payments/${paymentId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as PaymentsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Payments at path '${`payments/${paymentId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Payments items from collection payments
 
  Read Operation Details: Anyone can read payment records (public, links a listing payment to its onchain transfer).
  
 */
export async function getManyPayments(filter: string = ""): Promise<PaymentsResponse[]> {
  try {
    const results = await get("payments", { prompt: filter });
    return Array.isArray(results) ? results as PaymentsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Payments collection at path 'payments': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Payments collection at payments
 
  Read Operation Details: Anyone can read payment records (public, links a listing payment to its onchain transfer).
  
 */
export function subscribeManyPayments(callback: (data: PaymentsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("payments", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as PaymentsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Payments collection at path '${"payments"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Payments items from collection payments
 
  Read Operation Details: Anyone can read payment records (public, links a listing payment to its onchain transfer).
  
 */
export async function getAllPayments(filter: string = ""): Promise<PaymentsResponse[]> {
  try {
    const results = await get("payments", { prompt: filter });
    return Array.isArray(results) ? results as PaymentsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Payments collection at path 'payments': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Payments collection at payments
 
  Read Operation Details: Anyone can read payment records (public, links a listing payment to its onchain transfer).
  
 */
export function subscribeAllPayments(callback: (data: PaymentsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("payments", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as PaymentsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Payments collection at path '${"payments"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count Payments items in collection payments.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countPayments(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("payments", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting Payments at path 'payments': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on Payments items in collection payments.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregatePayments(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("payments", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating Payments at path 'payments': ${formatError(error)}`);
    return { value: 0 };
  }
}

